import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { Heart } from "lucide-react";
import PetsnapLogo from "@/components/PetsnapLogo";

export default function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const result =
      mode === "login"
        ? await login(email, password)
        : await register(name, email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      toast.error(result.error);
    } else {
      toast.success(mode === "login" ? "Welcome back!" : "Welcome to Petsnap!");
    }
  };

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden">
      {/* Left decorative panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1655306963086-a34411c0915b?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D232E]/70 via-[#2D232E]/10 to-transparent" />
        <div className="relative z-10 text-white max-w-md">
          <div className="inline-flex items-center gap-2 bg-white/95 text-[#2D232E] pl-1.5 pr-4 py-1 rounded-full mb-4">
            <PetsnapLogo className="w-6 h-6" />
            <span className="font-hand text-lg text-[#E76F51]">Petsnap</span>
          </div>
          <h2 className="font-display text-5xl font-bold mb-3 leading-tight">
            A cozy home for every paw print
          </h2>
          <p className="text-white/90 text-lg">
            Cats, dogs, and everyone in between — upload, organize, and rediscover their sweetest moments.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <PetsnapLogo className="w-9 h-9" />
            <span className="font-display text-2xl font-bold text-[#2D232E]">Petsnap</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2D232E] mb-2 tracking-tight">
            {mode === "login" ? "Welcome back" : "Join the pack"}
          </h1>
          <p className="font-hand text-2xl text-[#E76F51] mb-8">
            {mode === "login" ? "your friends missed you" : "let's meet your fluff!"}
          </p>

          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full bg-[#F4EFE6] rounded-full p-1 h-12 mb-6">
              <TabsTrigger
                data-testid={AUTH.loginTab}
                value="login"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#E76F51] data-[state=active]:shadow-sm font-semibold"
              >
                Log in
              </TabsTrigger>
              <TabsTrigger
                data-testid={AUTH.registerTab}
                value="register"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#E76F51] data-[state=active]:shadow-sm font-semibold"
              >
                Sign up
              </TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="space-y-4 mt-0">
                <div>
                  <Label className="text-[#6A606C] font-medium mb-1.5 block">Your name</Label>
                  <Input
                    data-testid={AUTH.nameInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={mode === "register"}
                    placeholder="Jane Doggo"
                    className="rounded-xl h-12 bg-white border-[#EAE2D6] focus-visible:ring-2 focus-visible:ring-[#E76F51]/40"
                  />
                </div>
              </TabsContent>

              <div>
                <Label className="text-[#6A606C] font-medium mb-1.5 block">Email</Label>
                <Input
                  data-testid={AUTH.emailInput}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@pawmail.com"
                  className="rounded-xl h-12 bg-white border-[#EAE2D6] focus-visible:ring-2 focus-visible:ring-[#E76F51]/40"
                />
              </div>
              <div>
                <Label className="text-[#6A606C] font-medium mb-1.5 block">Password</Label>
                <Input
                  data-testid={AUTH.passwordInput}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="rounded-xl h-12 bg-white border-[#EAE2D6] focus-visible:ring-2 focus-visible:ring-[#E76F51]/40"
                />
              </div>

              {error && (
                <div
                  data-testid={AUTH.errorMsg}
                  className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
                >
                  {error}
                </div>
              )}

              <Button
                data-testid={AUTH.submitBtn}
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold text-base transition-all hover:scale-[1.02] shadow-[0_8px_20px_rgba(231,111,81,0.25)]"
              >
                {busy ? "One moment..." : mode === "login" ? "Log in" : "Create account"}
              </Button>
            </form>
          </Tabs>

          <p className="text-center text-sm text-[#6A606C] mt-8 inline-flex items-center gap-1 justify-center w-full">
            Made with <Heart className="w-3.5 h-3.5 fill-[#E76F51] text-[#E76F51]" /> for good boys & girls
          </p>
        </div>
      </div>
    </div>
  );
}
